import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ReceiptIcon, ClockIcon, TrendingUpIcon, AlertTriangleIcon, ArrowUpRightIcon, PlaneIcon, DollarSignIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import { NIGERIAN_AIRLINES, MOCK_INVOICES } from '../../constants/data';
import { formatNaira } from '../../utils/formatCurrency';
import { exportToCSV, exportToJSON, printTable } from '../../utils/exportUtils';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { MetricGrid } from '../../components/features/MetricCard';
import { Table } from '../../components/ui/Table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// Mock chart data
const revenueData = [{
  date: 'Jan 1',
  refunds: 45,
  revenue: 2100000
}, {
  date: 'Jan 8',
  refunds: 52,
  revenue: 2450000
}, {
  date: 'Jan 15',
  refunds: 48,
  revenue: 2280000
}, {
  date: 'Jan 22',
  refunds: 61,
  revenue: 2890000
}, {
  date: 'Jan 29',
  refunds: 55,
  revenue: 2600000
}, {
  date: 'Feb 5',
  refunds: 67,
  revenue: 3150000
}, {
  date: 'Feb 12',
  refunds: 72,
  revenue: 3400000
}];
// Mock recent refunds
const recentRefunds = [{
  id: 'REF-001',
  ticket: 'ABC123',
  passenger: 'Chioma Okonkwo',
  amount: 45000,
  status: 'completed',
  time: '5 min ago'
}, {
  id: 'REF-002',
  ticket: 'DEF456',
  passenger: 'Emeka Eze',
  amount: 52000,
  status: 'processing',
  time: '12 min ago'
}, {
  id: 'REF-003',
  ticket: 'GHI789',
  passenger: 'Ngozi Nwosu',
  amount: 38000,
  status: 'completed',
  time: '25 min ago'
}, {
  id: 'REF-004',
  ticket: 'JKL012',
  passenger: 'Adebayo Ogundimu',
  amount: 61000,
  status: 'pending',
  time: '32 min ago'
}, {
  id: 'REF-005',
  ticket: 'MNO345',
  passenger: 'Fatima Mohammed',
  amount: 48000,
  status: 'completed',
  time: '45 min ago'
}];
export function AdminDashboard() {
  // Calculate total amount owed
  const totalAmountOwed = NIGERIAN_AIRLINES.reduce((sum, airline) => sum + airline.amountOwed, 0);
  const metrics = [{
    label: 'Total Refunds',
    value: '10,524',
    change: {
      value: 12.5,
      trend: 'up' as const
    },
    icon: <ReceiptIcon className="w-5 h-5" data-id="element-591" />,
    iconColor: 'bg-primary-100 text-primary-600'
  }, {
    label: 'Amount Owed by Airlines',
    value: formatNaira(totalAmountOwed, {
      compact: true
    }),
    change: {
      value: -5.3,
      trend: 'down' as const
    },
    icon: <DollarSignIcon className="w-5 h-5" data-id="element-592" />,
    iconColor: 'bg-warning-100 text-warning-600'
  }, {
    label: 'Pending Refunds',
    value: '23',
    change: {
      value: -8.2,
      trend: 'down' as const
    },
    icon: <ClockIcon className="w-5 h-5" data-id="element-593" />,
    iconColor: 'bg-error-100 text-error-600'
  }, {
    label: 'Avg. Processing Time',
    value: '24 min',
    change: {
      value: -15.0,
      trend: 'down' as const
    },
    icon: <ClockIcon className="w-5 h-5" data-id="element-594" />,
    iconColor: 'bg-accent-100 text-accent-600'
  }];
  // Export handlers
  const handleExportRefunds = (format: 'csv' | 'json') => {
    const exportData = recentRefunds.map(refund => ({
      'Refund ID': refund.id,
      Ticket: refund.ticket,
      Passenger: refund.passenger,
      Amount: formatNaira(refund.amount),
      Status: refund.status,
      Time: refund.time
    }));
    if (format === 'csv') {
      exportToCSV(exportData, `refunds-${new Date().toISOString().split('T')[0]}`);
      toast.success('Refunds exported to CSV');
    } else {
      exportToJSON(exportData, `refunds-${new Date().toISOString().split('T')[0]}`);
      toast.success('Refunds exported to JSON');
    }
  };
  const handleExportAirlines = (format: 'csv' | 'json') => {
    const exportData = NIGERIAN_AIRLINES.map(airline => ({
      Airline: airline.name,
      Code: airline.code,
      'Total Refunds': airline.totalRefunds,
      'Amount Owed': formatNaira(airline.amountOwed),
      'Avg Refund Time (min)': airline.avgRefundTime,
      'Compliance Score': `${airline.complianceScore}%`
    }));
    if (format === 'csv') {
      exportToCSV(exportData, `airlines-${new Date().toISOString().split('T')[0]}`);
      toast.success('Airlines exported to CSV');
    } else {
      exportToJSON(exportData, `airlines-${new Date().toISOString().split('T')[0]}`);
      toast.success('Airlines exported to JSON');
    }
  };
  const handlePrintRefunds = () => {
    const printData = recentRefunds.map(refund => ({
      ID: refund.id,
      Ticket: refund.ticket,
      Passenger: refund.passenger,
      Amount: formatNaira(refund.amount),
      Status: refund.status,
      Time: refund.time
    }));
    printTable('Recent Refunds', printData);
    toast.success('Opening print dialog...');
  };
  const refundColumns = [{
    key: 'ticket',
    header: 'Ticket',
    mobileLabel: 'Ticket'
  }, {
    key: 'passenger',
    header: 'Passenger',
    mobileLabel: 'Passenger'
  }, {
    key: 'amount',
    header: 'Amount',
    mobileLabel: 'Amount',
    render: (value: unknown) => <span className="tabular-nums font-semibold" data-id="element-595">
          {formatNaira(value as number)}
        </span>
  }, {
    key: 'status',
    header: 'Status',
    mobileLabel: 'Status',
    render: (value: unknown) => {
      const status = value as string;
      return <StatusBadge status={status === 'completed' ? 'complete' : status === 'processing' ? 'processing' : 'pending'} size="sm" data-id="element-596">
            {status}
          </StatusBadge>;
    }
  }, {
    key: 'time',
    header: 'Time',
    mobileLabel: 'Time',
    hideOnMobile: true
  }];
  const airlineColumns = [{
    key: 'name',
    header: 'Airline',
    mobileLabel: 'Airline',
    render: (_: unknown, row: (typeof NIGERIAN_AIRLINES)[0]) => <div className="flex items-center gap-2" data-id="element-597">
          <div className="w-8 h-8 bg-surface-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-598">
            <PlaneIcon className="w-4 h-4 text-surface-600" data-id="element-599" />
          </div>
          <span className="font-medium" data-id="element-600">{row.name}</span>
        </div>
  }, {
    key: 'totalRefunds',
    header: 'Refunds',
    mobileLabel: 'Refunds',
    render: (value: unknown) => <span className="tabular-nums" data-id="element-601">
          {(value as number).toLocaleString()}
        </span>
  }, {
    key: 'amountOwed',
    header: 'Amount Owed',
    mobileLabel: 'Owed',
    render: (value: unknown, row: (typeof NIGERIAN_AIRLINES)[0]) => {
      const amount = value as number;
      const isOverdue = row.id === 'arik-air';
      return <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2" data-id="element-602">
            <span className={`text-body-sm sm:text-body-md font-semibold tabular-nums ${amount === 0 ? 'text-success-600' : isOverdue ? 'text-error-600' : 'text-warning-600'}`} data-id="element-603">
              {formatNaira(amount)}
            </span>
            {amount > 0 && <span className={`px-2 py-0.5 rounded-full text-caption font-medium inline-block ${isOverdue ? 'bg-error-100 text-error-700' : 'bg-warning-100 text-warning-700'}`} data-id="element-604">
                {isOverdue ? 'Overdue' : 'Pending'}
              </span>}
          </div>;
    }
  }, {
    key: 'avgRefundTime',
    header: 'Avg. Time',
    mobileLabel: 'Avg Time',
    hideOnMobile: true,
    render: (value: unknown) => `${value} min`
  }, {
    key: 'complianceScore',
    header: 'Compliance',
    mobileLabel: 'Compliance',
    render: (value: unknown) => {
      const score = value as number;
      return <div className="flex items-center gap-2" data-id="element-605">
            <div className="w-12 sm:w-16 h-2 bg-surface-200 rounded-full overflow-hidden" data-id="element-606">
              <div className={`h-full rounded-full ${score >= 90 ? 'bg-success-500' : score >= 70 ? 'bg-warning-500' : 'bg-error-500'}`} style={{
            width: `${score}%`
          }} data-id="element-607" />
            </div>
            <span className="text-body-sm tabular-nums" data-id="element-608">{score}%</span>
          </div>;
    }
  }];
  return <div className="space-y-6 sm:space-y-8" data-id="element-609">
      {/* Metrics */}
      <MetricGrid metrics={metrics} data-id="element-610" />

      {/* Charts & Tables Row */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6" data-id="element-611">
        {/* Revenue Chart */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-612">
          <Card variant="elevated" padding="md" data-id="element-613">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6" data-id="element-614">
              <h2 className="text-heading-md sm:text-heading-lg text-surface-900" data-id="element-615">
                Refund Volume
              </h2>
              <span className="text-body-sm text-surface-500" data-id="element-616">
                Last 30 days
              </span>
            </div>
            <div className="h-48 sm:h-64" data-id="element-617">
              <ResponsiveContainer width="100%" height="100%" data-id="element-618">
                <LineChart data={revenueData} data-id="element-619">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8ECF1" data-id="element-620" />
                  <XAxis dataKey="date" tick={{
                  fontSize: 11
                }} stroke="#A8B5C8" data-id="element-621" />
                  <YAxis tick={{
                  fontSize: 11
                }} stroke="#A8B5C8" data-id="element-622" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E8ECF1',
                  borderRadius: '8px',
                  fontSize: '12px'
                }} data-id="element-623" />
                  <Line type="monotone" dataKey="refunds" stroke="#0066FF" strokeWidth={2} dot={{
                  fill: '#0066FF',
                  strokeWidth: 2
                }} data-id="element-624" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        {/* Alerts */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-625">
          <Card variant="elevated" padding="md" data-id="element-626">
            <div className="flex items-center justify-between mb-6" data-id="element-627">
              <h2 className="text-heading-md sm:text-heading-lg text-surface-900" data-id="element-628">
                Alerts
              </h2>
              <Link to={ROUTES.ADMIN_FRAUD} data-id="element-629">
                <Button variant="ghost" size="sm" icon={<ArrowUpRightIcon className="w-4 h-4" data-id="element-631" />} iconPosition="right" className="hidden sm:flex" data-id="element-630">
                  View All
                </Button>
                <Button variant="ghost" size="sm" icon={<ArrowUpRightIcon className="w-4 h-4" data-id="element-633" />} className="sm:hidden" data-id="element-632" />
              </Link>
            </div>
            <div className="space-y-3 sm:space-y-4" data-id="element-634">
              <div className="flex items-start gap-3 p-3 bg-error-50 border border-error-100 rounded-lg" data-id="element-635">
                <AlertTriangleIcon className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" data-id="element-636" />
                <div className="min-w-0 flex-1" data-id="element-637">
                  <p className="text-body-sm font-medium text-error-700" data-id="element-638">
                    High Priority Alert
                  </p>
                  <p className="text-body-sm text-error-600 break-words" data-id="element-639">
                    Multiple refund requests from same IP
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-warning-50 border border-warning-100 rounded-lg" data-id="element-640">
                <AlertTriangleIcon className="w-5 h-5 text-warning-500 flex-shrink-0 mt-0.5" data-id="element-641" />
                <div className="min-w-0 flex-1" data-id="element-642">
                  <p className="text-body-sm font-medium text-warning-700" data-id="element-643">
                    Invoice Overdue
                  </p>
                  <p className="text-body-sm text-warning-600 break-words" data-id="element-644">
                    Arik Air - INV-2024-0002
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-primary-50 border border-primary-100 rounded-lg" data-id="element-645">
                <ClockIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" data-id="element-646" />
                <div className="min-w-0 flex-1" data-id="element-647">
                  <p className="text-body-sm font-medium text-primary-700" data-id="element-648">
                    Processing Delay
                  </p>
                  <p className="text-body-sm text-primary-600 break-words" data-id="element-649">
                    3 refunds pending {'>'} 30 minutes
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Recent Refunds */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4
    }} data-id="element-650">
        <Card variant="elevated" padding="none" data-id="element-651">
          <div className="p-4 sm:p-6 border-b border-surface-100" data-id="element-652">
            <div className="flex items-center justify-between" data-id="element-653">
              <h2 className="text-heading-md sm:text-heading-lg text-surface-900" data-id="element-654">
                Recent Refunds
              </h2>
              <div className="flex items-center gap-2" data-id="element-655">
                <Button variant="ghost" size="sm" icon={<DownloadIcon className="w-4 h-4" data-id="element-657" />} onClick={() => handleExportRefunds('csv')} data-id="element-656">
                  <span className="hidden sm:inline" data-id="element-658">Export CSV</span>
                </Button>
                <Button variant="ghost" size="sm" icon={<FileTextIcon className="w-4 h-4" data-id="element-660" />} onClick={handlePrintRefunds} className="hidden sm:flex" data-id="element-659">
                  Print
                </Button>
                <Link to={ROUTES.ADMIN_REFUNDS} data-id="element-661">
                  <Button variant="ghost" size="sm" icon={<ArrowUpRightIcon className="w-4 h-4" data-id="element-663" />} iconPosition="right" data-id="element-662">
                    <span className="hidden sm:inline" data-id="element-664">View All</span>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-0" data-id="element-665">
            <Table columns={refundColumns} data={recentRefunds} keyExtractor={row => row.id} data-id="element-666" />
          </div>
        </Card>
      </motion.div>

      {/* Airline Performance */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5
    }} data-id="element-667">
        <Card variant="elevated" padding="none" data-id="element-668">
          <div className="p-4 sm:p-6 border-b border-surface-100" data-id="element-669">
            <div className="flex items-center justify-between" data-id="element-670">
              <h2 className="text-heading-md sm:text-heading-lg text-surface-900" data-id="element-671">
                Airline Performance
              </h2>
              <div className="flex items-center gap-2" data-id="element-672">
                <Button variant="ghost" size="sm" icon={<DownloadIcon className="w-4 h-4" data-id="element-674" />} onClick={() => handleExportAirlines('csv')} data-id="element-673">
                  <span className="hidden sm:inline" data-id="element-675">Export</span>
                </Button>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-0" data-id="element-676">
            <Table columns={airlineColumns} data={NIGERIAN_AIRLINES} keyExtractor={row => row.id} data-id="element-677" />
          </div>
        </Card>
      </motion.div>
    </div>;
}